import { supabase } from '../lib/supabase';

export interface DocumentInfo {
  url: string;
  name: string;
  type: 'cv' | 'cover_letter' | 'portfolio' | 'certificate' | 'other';
  description?: string;
  fileSize?: number;
  mimeType?: string;
}

export interface CreateApplicationParams {
  title?: string;
  letter: string;
  // Enhanced document structure with metadata
  documents: DocumentInfo[]; // Document info with metadata
  productId?: string;
  companyId?: string;
}

export interface ApplicationRecord {
  id: string;
  user_id: string;
  title: string | null;
  letter: string | null;
  documents: DocumentInfo[]; // Enhanced document structure
  status: string;
  product_id?: string | null;
  company_id?: string | null;
  ai_application?: boolean; // Indicates if this application was generated by AI
  created_at: string;
  updated_at: string;
  // Interview management fields
  interview_status?: 'not_scheduled' | 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  interview_date?: string | null;
  interview_duration?: number;
  interview_type?: 'in_person' | 'video_call' | 'phone_call' | 'hybrid';
  interview_location?: string | null;
  video_call_link?: string | null;
  interview_notes?: string | null;
  interview_outcome?: 'pending' | 'passed' | 'failed' | 'needs_follow_up' | 'hired' | 'rejected' | null;
  interview_feedback?: string | null;
  interview_rating?: number | null;
  next_steps?: string | null;
  interviewer_id?: string | null;
  reminder_sent?: boolean;
  interview_confirmed?: boolean;
  interview_confirmed_at?: string | null;
  reschedule_count?: number;
  interview_completed_at?: string | null;
}

export class ApplicationService {
  static async createApplication(params: CreateApplicationParams): Promise<ApplicationRecord> {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError) {
      throw authError;
    }
    const user = authData?.user;
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Normalize documents: ensure each entry has proper URL and metadata
    const normalizedDocuments: DocumentInfo[] = [];
    for (const doc of params.documents ?? []) {
      if (!doc || !doc.url) continue;
      
      let finalUrl = doc.url;
      
      // If it's a bare storage path (no scheme), treat as user-documents path
      const looksLikePath = !/^https?:\/\//i.test(doc.url);
      if (looksLikePath) {
        const url = await ApplicationService.getSignedUrlForUserDocument(doc.url);
        if (url) finalUrl = url;
      }
      
      // Ensure we have a valid URL
      if (finalUrl && (finalUrl.includes('/user-documents/') || finalUrl.includes('supabase.co'))) {
        normalizedDocuments.push({
          url: finalUrl,
          name: doc.name || doc.url.split('/').pop() || 'document',
          type: doc.type || 'other',
          description: doc.description,
          fileSize: doc.fileSize,
          mimeType: doc.mimeType
        });
      }
    }

    const payload = {
      user_id: user.id,
      title: params.title ?? null,
      letter: params.letter,
      documents: normalizedDocuments,
      status: 'submitted' as const,
      product_id: params.productId ?? null,
      company_id: params.companyId ?? null,
    };

    const { data, error } = await supabase
      .from('applications')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    return data as ApplicationRecord;
  }

  private static async getSignedUrlForUserDocument(filePath: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from('user-documents')
        .createSignedUrl(filePath, 3600);
      if (error) return null;
      return data?.signedUrl ?? null;
    } catch {
      return null;
    }
  }

  static async getSubmittedByUser(
    userId: string, 
    page: number = 1, 
    limit: number = 5, 
    aiGeneratedOnly: boolean = false,
    statusFilter: string = 'all'
  ): Promise<{ data: any[]; total: number; totalPages: number }> {
    const offset = (page - 1) * limit;
    
    // Build the query
    let query = supabase
      .from('applications')
      .select(`
        *,
        product:products(id, name, images, company_id),
        applicant:users(id, alias, first_name, last_name),
        meetings:meetings(id, start_at, end_at, duration_minutes, timezone, interview_type, location_address, meeting_link, phone_number, notes, documents, status, created_at, updated_at)
      `, { count: 'exact' })
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    // Apply AI filter if requested
    if (aiGeneratedOnly) {
      query = query.eq('ai_application', true);
    }

    // Apply status filter if requested
    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    
    if (error) throw error;
    
    const total = count || 0;
    const totalPages = Math.ceil(total / limit);
    
    // Transform the data to include the first meeting as 'meeting' property for backward compatibility
    const transformedData = (data || []).map(app => ({
      ...app,
      meeting: app.meetings && app.meetings.length > 0 ? {
        id: app.meetings[0].id,
        interviewType: app.meetings[0].interview_type,
        date: app.meetings[0].start_at ? new Date(app.meetings[0].start_at).toISOString().split('T')[0] : '',
        time: app.meetings[0].start_at ? new Date(app.meetings[0].start_at).toTimeString().split(' ')[0].substring(0, 5) : '',
        timezone: app.meetings[0].timezone,
        location: app.meetings[0].location_address,
        videoLink: app.meetings[0].meeting_link,
        phoneNumber: app.meetings[0].phone_number,
        notes: app.meetings[0].notes,
        documents: app.meetings[0].documents || [],
        status: app.meetings[0].status,
        createdAt: app.meetings[0].created_at,
        updatedAt: app.meetings[0].updated_at,
      } : undefined
    }));
    
    return {
      data: transformedData,
      total,
      totalPages
    };
  }

  static async getReceivedByCompany(companyId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('applications')
      .select(`
        *,
        product:products(id, name, images, company_id),
        applicant:users(id, alias, first_name, last_name),
        meetings:meetings(id, start_at, end_at, duration_minutes, timezone, interview_type, location_address, meeting_link, phone_number, notes, documents, status, created_at, updated_at)
      `)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    
    // Transform the data to include the first meeting as 'meeting' property for backward compatibility
    return (data || []).map(app => ({
      ...app,
      meeting: app.meetings && app.meetings.length > 0 ? {
        id: app.meetings[0].id,
        interviewType: app.meetings[0].interview_type,
        date: app.meetings[0].start_at ? new Date(app.meetings[0].start_at).toISOString().split('T')[0] : '',
        time: app.meetings[0].start_at ? new Date(app.meetings[0].start_at).toTimeString().split(' ')[0].substring(0, 5) : '',
        timezone: app.meetings[0].timezone,
        location: app.meetings[0].location_address,
        videoLink: app.meetings[0].meeting_link,
        phoneNumber: app.meetings[0].phone_number,
        notes: app.meetings[0].notes,
        documents: app.meetings[0].documents || [],
        status: app.meetings[0].status,
        createdAt: app.meetings[0].created_at,
        updatedAt: app.meetings[0].updated_at,
      } : undefined
    }));
  }

  static async updateApplicationStatus(applicationId: string, status: 'accepted' | 'rejected' | 'review' | 'submitted'): Promise<ApplicationRecord> {
    const { data, error } = await supabase
      .from('applications')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    return data as ApplicationRecord;
  }

  static async scheduleInterview(applicationId: string, interviewData: {
    interview_date: string;
    interview_duration?: number;
    interview_type?: 'in_person' | 'video_call' | 'phone_call' | 'hybrid';
    interview_location?: string;
    video_call_link?: string;
    interviewer_id?: string;
    interview_notes?: string;
  }): Promise<ApplicationRecord> {
    const { data, error } = await supabase
      .from('applications')
      .update({
        interview_status: 'scheduled',
        interview_date: interviewData.interview_date,
        interview_duration: interviewData.interview_duration || 60,
        interview_type: interviewData.interview_type || 'in_person',
        interview_location: interviewData.interview_location || null,
        video_call_link: interviewData.video_call_link || null,
        interviewer_id: interviewData.interviewer_id || null,
        interview_notes: interviewData.interview_notes || null,
        interview_confirmed: false,
        reminder_sent: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    return data as ApplicationRecord;
  }

  static async updateInterviewOutcome(applicationId: string, outcome: {
    interview_outcome: 'pending' | 'passed' | 'failed' | 'needs_follow_up' | 'hired' | 'rejected';
    interview_rating?: number;
    interview_feedback?: string;
    interview_notes?: string;
    next_steps?: string;
  }): Promise<ApplicationRecord> {
    const { data, error } = await supabase
      .from('applications')
      .update({
        interview_status: 'completed',
        interview_outcome: outcome.interview_outcome,
        interview_rating: outcome.interview_rating || null,
        interview_feedback: outcome.interview_feedback || null,
        interview_notes: outcome.interview_notes || null,
        next_steps: outcome.next_steps || null,
        interview_completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    return data as ApplicationRecord;
  }

  static async confirmInterview(applicationId: string): Promise<ApplicationRecord> {
    const { data, error } = await supabase
      .from('applications')
      .update({
        interview_confirmed: true,
        interview_confirmed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    return data as ApplicationRecord;
  }

  static async rescheduleInterview(applicationId: string, newDate: string, reason?: string): Promise<ApplicationRecord> {
    const { data: currentApp, error: fetchError } = await supabase
      .from('applications')
      .select('reschedule_count, interview_notes')
      .eq('id', applicationId)
      .single();

    if (fetchError) {
      throw fetchError;
    }

    const { data, error } = await supabase
      .from('applications')
      .update({
        interview_status: 'rescheduled',
        interview_date: newDate,
        interview_confirmed: false,
        interview_confirmed_at: null,
        reschedule_count: (currentApp.reschedule_count || 0) + 1,
        interview_notes: reason ? `${currentApp.interview_notes || ''}\n\nRescheduled: ${reason}`.trim() : currentApp.interview_notes,
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    return data as ApplicationRecord;
  }
}


